import { defineStore } from 'pinia';
import {
  login as userLogin,
  logout as userLogout,
  getUserInfo,
  LoginData,
} from '@/api/user';
import { setToken, clearToken } from '@/utils/auth';
import { removeRouteListener } from '@/utils/route-listener';
import { UserState } from './types';
import useAppStore from '../app';

// 用户信息本地存储的key
const USER_INFO_KEY = 'userInfo';

const useUserStore = defineStore('user', {
  state: (): UserState => {
    // 从本地存储读取用户信息
    const savedUserInfo = localStorage.getItem(USER_INFO_KEY);
    const defaultState: UserState = {
      avatar: '',
      id: 0,
      user_id: 0,
      group_id: 0,
      user_role: 0,
      region_id: 0,
      pid: 0,
      username: '',
      nickname: '',
      mobile: '',
      department: 0,
      createtime: 0,
      expiretime: 0,
      expires_in: 0,
      staff_id: 0,
      role: '',
      token: '',
    };

    if (savedUserInfo) {
      try {
        return { ...defaultState, ...JSON.parse(savedUserInfo) };
      } catch (error) {
        console.error('Failed to parse saved user info:', error);
        return defaultState;
      }
    }

    return defaultState;
  },

  getters: {
    userInfo(state: UserState): UserState {
      return { ...state };
    },
    // 检查用户是否已登录
    isLoggedIn(state: UserState): boolean {
      return !!(state.token && state.id);
    },
  },

  actions: {
    // switchRoles() {
    //   return new Promise((resolve) => {
    //     this.role = this.role === 'user' ? 'admin' : 'user';
    //     resolve(this.role);
    //   });
    // },
    // Set user's information
    setInfo(partial: Partial<UserState>) {
      this.$patch(partial);
      // 保存用户信息到本地存储
      const currentState = this.$state;
      const updatedState = { ...currentState, ...partial };
      localStorage.setItem(USER_INFO_KEY, JSON.stringify(updatedState));
    },

    // Reset user's information
    resetInfo() {
      this.$reset();
      // 清除本地存储的用户信息
      localStorage.removeItem(USER_INFO_KEY);
    },

    // Get user's information
    async info() {
      const res = await getUserInfo();

      this.setInfo(res.data);
    },

    // Login
    async login(loginForm: LoginData) {
      try {
        const res = await userLogin(loginForm);
        setToken(res.data.userinfo.token);
      } catch (err) {
        clearToken();
        throw err;
      }
    },

    // 清理所有用户相关数据
    clearAllUserData() {
      const appStore = useAppStore();

      // 重置用户信息
      this.resetInfo();

      // 清除token
      clearToken();

      // 移除路由监听器
      removeRouteListener();

      // 清除服务器菜单
      appStore.clearServerMenu();

      // 清除其他可能存在的用户相关数据
      localStorage.removeItem('arco-theme');
      sessionStorage.clear();

      // 清除可能的cookie
      document.cookie.split(';').forEach((c) => {
        document.cookie = c
          .replace(/^ +/, '')
          .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
      });
    },

    logoutCallBack() {
      this.clearAllUserData();
    },

    // Logout
    async logout() {
      try {
        // 尝试调用后端退出接口
        await userLogout();
      } catch (error) {
        // 即使后端退出失败，也要清理前端状态
        console.warn(
          'Backend logout failed, but clearing frontend state:',
          error
        );
      } finally {
        // 无论后端是否成功，都要清理前端状态
        this.logoutCallBack();
      }
    },

    // 强制退出（不调用后端接口）
    forceLogout() {
      this.logoutCallBack();
    },
  },
});

export default useUserStore;
